import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { EnvelopeSimple, MapPin, PaperPlaneTilt } from "@phosphor-icons/react";
import { API_BASE, SITE } from "../lib/site";
import { resolveInquiry } from "../lib/inquiryTopics";
import Seo from "../components/Seo";

const Contact = () => {
  const [searchParams] = useSearchParams();
  const initialInquiry = resolveInquiry(searchParams.get("topic"));

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: initialInquiry.key === "general" ? "" : initialInquiry.label,
    message: "",
  });
  const [inquiry, setInquiry] = useState(initialInquiry);
  const [submitting, setSubmitting] = useState(false);

  // Re-sync when the ?topic= query param changes (e.g., user navigates
  // from another CTA on the site).
  useEffect(() => {
    const next = resolveInquiry(searchParams.get("topic"));
    setInquiry(next);
    if (next.key !== "general") {
      setForm((f) => ({ ...f, subject: f.subject || next.label }));
    }
  }, [searchParams]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API_BASE}/contact`,
        {
          ...form,
          inquiry_type: inquiry.label,
          source_page:
            typeof window !== "undefined" ? window.location.href : null,
        },
        { withCredentials: false }
      );
      toast.success(
        "Message sent. A confirmation email is on its way to your inbox."
      );
      setForm({
        name: "",
        email: "",
        phone: "",
        subject: inquiry.key === "general" ? "" : inquiry.label,
        message: "",
      });
    } catch {
      toast.error("We couldn't send your message right now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <Seo
        title="Contact Us"
        description="Contact Begin Again Living Ministries for grief support, chaplain services, speaking engagements, or bulk-order questions. We're here to walk with you."
      />
      <section className="py-20 sm:py-28">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 reveal">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
              <span className="w-8 h-px bg-secondary" />
              Contact
            </span>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] text-ink-900">
              Reach out &mdash; we&rsquo;d love to hear from you.
            </h1>
            <p className="mt-6 text-ink-700 text-lg leading-relaxed">
              Every message is delivered directly to our ministry office at{" "}
              <span className="text-primary font-semibold">{SITE.email}</span>{" "}
              and a member of our team will respond as soon as possible.
            </p>

            <div className="mt-10 space-y-5 text-ink-700">
              <a
                href={`mailto:${SITE.email}`}
                data-testid="contact-email-link"
                className="flex items-start gap-3 hover:text-primary transition-colors break-all"
              >
                <EnvelopeSimple size={20} weight="duotone" className="text-primary mt-0.5 shrink-0" />
                <span className="leading-snug">{SITE.email}</span>
              </a>
              <address
                data-testid="contact-mailing-address"
                className="flex items-start gap-3 not-italic"
              >
                <MapPin size={20} weight="duotone" className="text-primary mt-0.5 shrink-0" />
                <span className="leading-snug">
                  {SITE.name}
                  <br />
                  {SITE.address.line1}
                  <br />
                  {SITE.address.line2}
                </span>
              </address>
            </div>
          </div>

          <div className="lg:col-span-7 reveal">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-white rounded-2xl border border-ink-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-8 sm:p-10"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap mb-7">
                <h2 className="font-heading text-2xl text-ink-900 font-medium">
                  Send a message
                </h2>
                {inquiry.key !== "general" && (
                  <span
                    data-testid="contact-inquiry-badge"
                    className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-primary font-semibold bg-primary/8 border border-primary/15 rounded-full px-3 py-1"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-secondary" />
                    {inquiry.label}
                  </span>
                )}
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-ink-800 mb-2">Name *</label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setField("name", e.target.value)}
                    data-testid="contact-input-name"
                    className="w-full rounded-xl border border-ink-200 px-4 py-3 bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-ink-800 mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                    data-testid="contact-input-email"
                    className="w-full rounded-xl border border-ink-200 px-4 py-3 bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                  />
                </div>
              </div>

              <div className="mt-5">
                <label className="block text-sm font-semibold text-ink-800 mb-2">
                  Phone (optional)
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setField("phone", e.target.value)}
                  data-testid="contact-input-phone"
                  autoComplete="tel"
                  className="w-full rounded-xl border border-ink-200 px-4 py-3 bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                />
              </div>

              <div className="mt-5">
                <label className="block text-sm font-semibold text-ink-800 mb-2">Subject (optional)</label>
                <input
                  type="text"
                  value={form.subject}
                  onChange={(e) => setField("subject", e.target.value)}
                  data-testid="contact-input-subject"
                  className="w-full rounded-xl border border-ink-200 px-4 py-3 bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                />
              </div>

              <div className="mt-5">
                <label className="block text-sm font-semibold text-ink-800 mb-2">Message *</label>
                <textarea
                  required
                  rows={6}
                  value={form.message}
                  onChange={(e) => setField("message", e.target.value)}
                  data-testid="contact-input-message"
                  className="w-full rounded-xl border border-ink-200 px-4 py-3 bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all resize-y"
                />
              </div>

              <p className="mt-4 text-xs text-ink-500 leading-relaxed">
                Your message is delivered directly to {SITE.email}. You&rsquo;ll
                receive a confirmation email as soon as it&rsquo;s on its way.
              </p>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-btn"
                className="mt-6 inline-flex items-center gap-2 bg-primary text-white rounded-full px-7 py-3.5 font-semibold hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 disabled:opacity-60"
              >
                <PaperPlaneTilt size={18} weight="duotone" />
                {submitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
